// Terrain chunk manager: asks the mesh worker for chunk geometry, picks a LOD per chunk from the
// camera, culls by frustum, evicts by distance, and draws each chunk at the ±WORLD_W copy nearest
// the camera so the east-west seam never shows.
//
//   camera height h ─┬─ h > WORLD_BAND ───────────────► one world mesh (LOD WORLD), 3 copies
//                    └─ else, per visible chunk copy ─┬─ near the target and h small ─► NEAR
//                                                     └─ otherwise ───────────────────► MID
//
// MID chunks for the whole world are preloaded (≈0.6M triangles, ~30 MB), so a NEAR chunk that is
// still building always has a MID fallback and the view never shows holes.

import * as THREE from 'three/webgpu';
import { HEX_W, ROWS, ROW_H, WORLD_W } from '../../core/grid.ts';
import type { ShownChunk } from '../features/layer.ts';
import type { Clearing, FeatureData } from '../features/placement.ts';
import type { PhysicalGrid } from '../hexdata.ts';
import { CHUNK_COUNT, CHUNK_SIZE, LOD, chunkCR, type Lod, type MeshData } from './mesher.ts';
import type { WorkerReply, WorkerRequest } from './worker.ts';

/** Camera height (hex radii) above which the single world mesh replaces chunks. */
export const WORLD_BAND = 240;
const NEAR_MAX_HEIGHT = 75;
const NEAR_RADIUS_MIN = 20;
const NEAR_RADIUS_PER_HEIGHT = 1.5;
const NEAR_CACHE = 48;
const MAX_IN_FLIGHT = 3;
/** NEAR chunks that start being drawn per frame: each uploads its geometry on its first draw. */
const SHOWN_PER_FRAME = 1;
const SHIFTS = [-1, 0, 1] as const;
const BOX_Y_MIN = -1.6;
const BOX_Y_MAX = 2.2;

/** Talks to the mesh worker; one promise per request. */
export class MeshClient {
  private readonly worker: Worker;
  private nextId = 1;
  private readonly waiting = new Map<number, { resolve: (m: MeshData) => void; reject: (e: Error) => void }>();

  constructor(grid: PhysicalGrid, clearings: readonly Clearing[] = []) {
    this.worker = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' });
    this.worker.onmessage = (e: MessageEvent<WorkerReply>) => {
      const reply = e.data;
      const w = this.waiting.get(reply.id);
      if (!w) {
        return;
      }
      this.waiting.delete(reply.id);
      if (reply.type === 'mesh') {
        w.resolve(reply.mesh);
      } else {
        w.reject(new Error(reply.message));
      }
    };

    // Compact copies: grid fields are views into one big file buffer.
    const copy = {} as Record<string, ArrayBufferView>;
    const transfer: Transferable[] = [];
    for (const [name, arr] of Object.entries(grid) as Array<[string, Uint8Array]>) {
      const c = arr.slice();
      copy[name] = c;
      transfer.push(c.buffer);
    }
    const init: WorkerRequest = { type: 'init', grid: copy as unknown as PhysicalGrid, clearings };
    this.worker.postMessage(init, transfer);
  }

  chunk(key: number, lod: Lod): Promise<MeshData> {
    return this.send((id) => ({ type: 'chunk', id, key, lod }));
  }

  world(): Promise<MeshData> {
    return this.send((id) => ({ type: 'world', id }));
  }

  dispose(): void {
    this.worker.terminate();
  }

  private send(make: (id: number) => WorkerRequest): Promise<MeshData> {
    const id = this.nextId++;
    return new Promise((resolve, reject) => {
      this.waiting.set(id, { resolve, reject });
      this.worker.postMessage(make(id));
    });
  }
}

/** BufferGeometry over worker output (no copies). */
export function toGeometry(m: MeshData): THREE.BufferGeometry {
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(m.position, 3));
  g.setAttribute('normal', new THREE.BufferAttribute(m.normal, 3));
  g.setAttribute('biomeA', new THREE.BufferAttribute(m.biomeA, 4, true));
  g.setAttribute('biomeB', new THREE.BufferAttribute(m.biomeB, 4, true));
  g.setAttribute('aux', new THREE.BufferAttribute(m.aux, 4, true));
  g.setAttribute('shape', new THREE.BufferAttribute(m.shape, 4, true));
  g.setIndex(new THREE.BufferAttribute(m.index, 1));
  const box = new THREE.Box3(new THREE.Vector3(m.box[0], m.box[1], m.box[2]), new THREE.Vector3(m.box[3], m.box[4], m.box[5]));
  g.boundingBox = box;
  g.boundingSphere = box.getBoundingSphere(new THREE.Sphere());
  return g;
}

interface Built {
  /** Unique per chunk and LOD. */
  id: number;
  key: number;
  geometry: THREE.BufferGeometry;
  features: FeatureData | null;
  mid: boolean;
  /** No triangles (open ocean only): never drawn. */
  empty: boolean;
  /** One mesh per shift (−W, 0, +W), created on first use. */
  meshes: Array<THREE.Mesh | null>;
  lastUsed: number;
}

interface Want {
  key: number;
  lod: Lod;
  dist: number;
}

export interface ChunkStats {
  visible: number;
  near: number;
  mid: number;
  world: boolean;
  pending: number;
}

export class TerrainChunks {
  readonly group = new THREE.Group();
  private readonly client: MeshClient;
  private readonly material: THREE.Material;
  private readonly mid = new Map<number, Built>();
  private readonly near = new Map<number, Built>();
  private world: Built | null = null;
  private readonly requested = new Set<string>();
  private inFlight = 0;
  /** Built NEAR chunks waiting for a frame to start being drawn. */
  private arrived: Built[] = [];
  private wants: Want[] = [];
  private frame = 0;
  private readonly frustum = new THREE.Frustum();
  private readonly projView = new THREE.Matrix4();
  private readonly boxes: THREE.Box3[] = [];
  private readonly shifted = new THREE.Box3();
  private shadowMode: 'on' | 'off' = 'on';
  private shownList: ShownChunk[] = [];
  private last: ChunkStats = { visible: 0, near: 0, mid: 0, world: false, pending: 0 };

  constructor(client: MeshClient, material: THREE.Material) {
    this.client = client;
    this.material = material;
    this.group.name = 'terrain';
    for (let key = 0; key < CHUNK_COUNT; key++) {
      const { cc, rc } = chunkCR(key);
      const x0 = cc * CHUNK_SIZE * HEX_W - HEX_W;
      const x1 = (cc + 1) * CHUNK_SIZE * HEX_W + HEX_W;
      const z0 = rc * CHUNK_SIZE * ROW_H - ROW_H;
      const z1 = Math.min(ROWS, (rc + 1) * CHUNK_SIZE) * ROW_H + ROW_H;
      this.boxes.push(new THREE.Box3(new THREE.Vector3(x0, BOX_Y_MIN, z0), new THREE.Vector3(x1, BOX_Y_MAX, z1)));
    }
  }

  /** Build the world mesh and every MID chunk, nearest to (x, z) first. */
  async preload(x: number, z: number): Promise<void> {
    const worldMesh = await this.client.world();
    this.world = { id: -1, key: -1, geometry: toGeometry(worldMesh), features: null, mid: false, empty: false, meshes: [null, null, null], lastUsed: 0 };
    const order = [...Array(CHUNK_COUNT).keys()].sort((a, b) => this.distTo(a, x, z) - this.distTo(b, x, z));
    await Promise.all(order.map((key) => this.request(key, LOD.MID)));
  }

  /** Receive-cast shadows on/off (chunks are both casters and receivers). */
  setShadows(mode: 'on' | 'off'): void {
    this.shadowMode = mode;
    this.group.traverse((o) => {
      o.castShadow = mode === 'on';
      o.receiveShadow = mode === 'on';
    });
  }

  /** Choose and show chunks for this camera. */
  update(camera: THREE.PerspectiveCamera, tx: number, tz: number, height: number): void {
    this.frame++;
    this.admit();
    this.projView.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
    this.frustum.setFromProjectionMatrix(this.projView);
    for (const o of this.group.children) {
      o.visible = false;
    }
    this.shownList = [];
    this.wants = [];
    const stats: ChunkStats = { visible: 0, near: 0, mid: 0, world: false, pending: 0 };

    if (height > WORLD_BAND && this.world) {
      stats.world = true;
      for (let s = 0; s < SHIFTS.length; s++) {
        this.shifted.copy(this.world.geometry.boundingBox as THREE.Box3).translate(new THREE.Vector3(SHIFTS[s] * WORLD_W, 0, 0));
        if (this.frustum.intersectsBox(this.shifted)) {
          this.show(this.world, s);
          stats.visible++;
        }
      }
      this.last = stats;
      return;
    }

    const nearRadius = Math.max(NEAR_RADIUS_MIN, height * NEAR_RADIUS_PER_HEIGHT);
    for (let key = 0; key < CHUNK_COUNT; key++) {
      for (let s = 0; s < SHIFTS.length; s++) {
        this.shifted.copy(this.boxes[key]).translate(new THREE.Vector3(SHIFTS[s] * WORLD_W, 0, 0));
        if (!this.frustum.intersectsBox(this.shifted)) {
          continue;
        }
        const dist = boxDistance2D(this.shifted, tx, tz);
        stats.visible++;
        if (height < NEAR_MAX_HEIGHT && dist < nearRadius) {
          const n = this.near.get(key);
          if (n) {
            this.show(n, s);
            stats.near++;
            continue;
          }
          this.wants.push({ key, lod: LOD.NEAR, dist });
          stats.pending++;
        }
        const m = this.mid.get(key);
        if (m) {
          this.show(m, s);
          stats.mid++;
        } else {
          this.wants.push({ key, lod: LOD.MID, dist });
          stats.pending++;
        }
      }
    }
    this.pump();
    this.evict(tx, tz);
    stats.pending += this.inFlight + this.arrived.length;
    this.last = stats;
  }

  stats(): ChunkStats {
    return this.last;
  }

  /**
   * Run `draw` with the world mesh at shift 0 as the only terrain drawn (the globe bakes the whole
   * map from above), then put the visibility back. False when the world mesh is not built yet.
   */
  withWorld(draw: () => void): boolean {
    if (!this.world) {
      return false;
    }
    const mesh = this.meshOf(this.world, SHIFTS.indexOf(0));
    const kids = [...this.group.children];
    const shown = kids.map((o) => o.visible);
    for (const o of kids) {
      o.visible = false;
    }
    mesh.visible = true;
    try {
      draw();
    } finally {
      kids.forEach((o, i) => {
        o.visible = shown[i];
      });
      mesh.visible = kids.includes(mesh) ? mesh.visible : false;
    }
    return true;
  }

  /** Chunks drawn this frame (with their props), and the copy shift each is drawn at. */
  shown(): readonly ShownChunk[] {
    return this.shownList;
  }

  /** Free every geometry and stop the mesh worker. */
  dispose(): void {
    for (const b of [...this.mid.values(), ...this.near.values(), ...this.arrived, ...(this.world ? [this.world] : [])]) {
      b.geometry.dispose();
    }
    this.group.clear();
    this.client.dispose();
  }

  private distTo(key: number, x: number, z: number): number {
    const b = this.boxes[key];
    let best = Infinity;
    for (const s of SHIFTS) {
      this.shifted.copy(b).translate(new THREE.Vector3(s * WORLD_W, 0, 0));
      best = Math.min(best, boxDistance2D(this.shifted, x, z));
    }
    return best;
  }

  private show(b: Built, s: number): void {
    b.lastUsed = this.frame;
    if (b.key >= 0) {
      this.shownList.push({ id: b.id, key: b.key, features: b.features, shift: SHIFTS[s], mid: b.mid });
    }
    if (b.empty) {
      return;
    }
    this.meshOf(b, s).visible = true;
  }

  /** The mesh of a built chunk at one shift, created on first use. */
  private meshOf(b: Built, s: number): THREE.Mesh {
    let mesh = b.meshes[s];
    if (!mesh) {
      mesh = new THREE.Mesh(b.geometry, this.material);
      mesh.position.x = SHIFTS[s] * WORLD_W;
      mesh.matrixAutoUpdate = false;
      mesh.updateMatrix();
      mesh.frustumCulled = false;
      mesh.castShadow = this.shadowMode === 'on';
      mesh.receiveShadow = this.shadowMode === 'on';
      b.meshes[s] = mesh;
      this.group.add(mesh);
    }
    return mesh;
  }

  /** Issue the nearest wanted builds, a few at a time. */
  private pump(): void {
    this.wants.sort((a, b) => a.dist - b.dist);
    for (const w of this.wants) {
      if (this.inFlight >= MAX_IN_FLIGHT) {
        return;
      }
      void this.request(w.key, w.lod);
    }
  }

  private async request(key: number, lod: Lod): Promise<void> {
    const id = `${key}:${lod}`;
    if (this.requested.has(id)) {
      return;
    }
    this.requested.add(id);
    this.inFlight++;
    try {
      const mesh = await this.client.chunk(key, lod);
      const built: Built = {
        id: key * 2 + lod,
        key,
        geometry: toGeometry(mesh),
        features: mesh.features ?? null,
        mid: lod === LOD.MID,
        empty: mesh.index.length === 0,
        meshes: [null, null, null],
        lastUsed: this.frame,
      };
      if (lod === LOD.NEAR) {
        this.arrived.push(built);
      } else {
        this.mid.set(key, built);
      }
    } finally {
      this.inFlight--;
    }
  }

  /** Let a few of the built NEAR chunks be drawn, so their uploads do not land in one frame. */
  private admit(): void {
    for (const b of this.arrived.splice(0, SHOWN_PER_FRAME)) {
      this.near.set(b.key, b);
    }
  }

  /** Drop the NEAR chunks farthest from the target beyond the cache size (never one drawn this frame). */
  private evict(tx: number, tz: number): void {
    if (this.near.size <= NEAR_CACHE) {
      return;
    }
    const old = [...this.near.entries()]
      .filter(([, b]) => b.lastUsed < this.frame)
      .map(([key, b]) => ({ key, b, dist: this.distTo(key, tx, tz) }))
      .sort((a, b) => b.dist - a.dist);
    for (const { key, b } of old.slice(0, this.near.size - NEAR_CACHE)) {
      for (const m of b.meshes) {
        if (m) {
          this.group.remove(m);
        }
      }
      b.geometry.dispose();
      this.near.delete(key);
      this.requested.delete(`${key}:${LOD.NEAR}`);
    }
  }
}

/** Horizontal distance from (x, z) to a box (0 inside). */
function boxDistance2D(b: THREE.Box3, x: number, z: number): number {
  const dx = Math.max(b.min.x - x, 0, x - b.max.x);
  const dz = Math.max(b.min.z - z, 0, z - b.max.z);
  return Math.hypot(dx, dz);
}
